export function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function formatPointsValue(points) {
  const n = toFiniteNumber(points);
  if (n == null) return null;
  return String(Number(n.toFixed(2)));
}

function trimZeros(n) {
  return Math.abs(n)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");
}

/** Weekly points change: +2.45 / −1.3. Zero and missing are hidden. */
export function formatPointsWeekDelta(delta) {
  const n = toFiniteNumber(delta);
  if (n == null || n === 0) return null;
  const sign = n > 0 ? "+" : "−";
  return {
    text: `${sign}${trimZeros(n)}`,
    tone: n > 0 ? "up" : "down",
  };
}

/** Weekly place change: climbed is up (green), dropped is down (red). */
export function formatPlaceWeekDelta(delta) {
  const n = toFiniteNumber(delta);
  if (n == null || n === 0) return null;
  const places = Math.abs(Math.round(n));
  return {
    text: n > 0 ? `↑ ${places}` : `↓ ${places}`,
    tone: n > 0 ? "up" : "down",
  };
}

/** Classement step: B2 → B1. Unchanged and missing stay hidden. */
export function formatClassementWeekDelta(ranking) {
  const step = toFiniteNumber(
    ranking?.classement_delta ?? ranking?.fltt_classement_delta
  );
  const from = String(
    ranking?.classement_from || ranking?.fltt_classement_from || ""
  )
    .trim()
    .toUpperCase();
  const to = String(
    ranking?.classement_to || ranking?.classement || ranking?.fltt_classement || ""
  )
    .trim()
    .toUpperCase();
  if (step == null || step === 0 || !from || !to || from === to) return null;
  return {
    text: `${from} → ${to}`,
    tone: step > 0 ? "up" : "down",
  };
}

export function formatRankingPlace(place) {
  const n = toFiniteNumber(place);
  if (n == null) return null;
  return `#${Math.round(n)}`;
}

/** Short week label for ranking chart axis / tooltip. */
export function formatRankingWeekLabel(weekStart, locale) {
  if (!weekStart) return "—";
  const d = new Date(`${String(weekStart).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(weekStart).slice(0, 10);
  return d.toLocaleDateString(locale || undefined, {
    day: "numeric",
    month: "short",
  });
}

/**
 * Chronological chart rows from FLTT ranking history (newest-first or oldest-first).
 * Place stays numeric so the chart can reverse the national axis (climbing looks up).
 */
export function buildFlttRankingChartData(history, locale) {
  if (!Array.isArray(history) || history.length === 0) return [];

  const rows = history
    .map((row) => {
      const weekStart = row?.week_start ? String(row.week_start).slice(0, 10) : null;
      const points = toFiniteNumber(row?.ranking_points);
      const place = toFiniteNumber(row?.ranking_place);
      const classement = String(row?.classement || "").trim() || null;
      if (points == null && place == null && !classement && !weekStart) return null;
      return {
        weekStart,
        weekLabel: formatRankingWeekLabel(weekStart, locale),
        classement,
        points,
        place: place == null ? null : Math.round(place),
        sortKey: weekStart || "",
      };
    })
    .filter(Boolean);

  rows.sort((a, b) => {
    if (a.sortKey && b.sortKey) return a.sortKey.localeCompare(b.sortKey);
    if (a.sortKey) return -1;
    if (b.sortKey) return 1;
    return 0;
  });

  return rows;
}

/** True when history has enough numeric samples for a dual-metric chart. */
export function canShowFlttRankingChart(history) {
  const data = buildFlttRankingChartData(history);
  if (data.length < 2) return false;
  const hasPoints = data.some((d) => d.points != null);
  const hasPlace = data.some((d) => d.place != null);
  return hasPoints || hasPlace;
}
